import { useContext, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import {
  addNewPricingForStorePropertyHelper,
  cleanObject,
  createVariantPropertiesHelper,
  deepCopyFunction,
  getRandomIntInclusive,
  removePricingForStorePropertyHelper,
  updateStorePropertiesHelper,
  updateVariantStorePropertiesHelper,
  addNewVariantOptionHelper,
  removeVariantPropertyHelper,
  addNewPricingPropertyForVariantStorePropertyHelper,
  removePricingPropertyForVariantStorePropertyHelper,
} from "../../../helpers";
import { RequestLoader, ToastContext } from "../../../hooks/context";
import {
  createCategory,
  deleteProductImage,
  reorderProductImages,
  uploadProductImage,
} from "../../../services/inventory";
import {
  useGetPricingTypes,
  useGetProductCategoryList,
  useGetStockUnits,
  useGetSuppliersForSelectField,
} from "../../../hooks/api/queries/useInventory";
import { v4 as uuidv4 } from "uuid";
import { useDeleteVariantFromBackend } from "../../../hooks/api/mutations/useInventory";
import { useGetSections } from "../../../hooks/api/queries/useSection";
import { useParams } from "react-router-dom";
import { useGetInfo } from "../../../hooks/useGetInfo";

const booleanLabel = {
  false: "No",
  true: "Yes",
};

const reLabelDefault = (stores, nameKey = "name", idKey = "id") => {
  let newArray = [];
  stores?.forEach((store) => {
    newArray.push({ value: store?.[idKey], label: store?.[nameKey] });
  });
  return newArray;
};

const reArrangeVariantStoreProperties = (
  variantStoreProperties,
  isEditMode,
  rootFormDetails = {},
) => {
  const newVariantStoreProperties = [];
  variantStoreProperties?.forEach((variantStoreProperty) => {
    const newStoreProperty = {
      ...variantStoreProperty,
      cost_price: `${isEditMode ? variantStoreProperty?.cost_price || 0 : variantStoreProperty?.cost_price || ""}`,
      returned_from_backend: true,
      pricings: variantStoreProperty?.pricings?.map((pricingProperty) => {
        return {
          price:
            rootFormDetails?.include_vat_in_price && rootFormDetails?.vat
              ? Number(pricingProperty?.price) +
                (Number(pricingProperty?.price) *
                  Number(rootFormDetails?.vat)) /
                  100
              : Number(pricingProperty?.price),
          // pricingProperty?.operation_unit_price || pricingProperty?.price,
          discount_rate: pricingProperty?.discount_rate,
          pricing_type_id: pricingProperty?.pricing_type?.id,
          uuid: uuidv4(),
        };
      }),
      minimum_stock_quantity:
        variantStoreProperty?.minimum_stock_quantity === null ||
        variantStoreProperty?.minimum_stock_quantity === undefined
          ? undefined
          : `${variantStoreProperty?.minimum_stock_quantity}`,
      stock_quantity:
        variantStoreProperty?.stock_quantity === null ||
        variantStoreProperty?.stock_quantity === undefined
          ? undefined
          : `${variantStoreProperty?.stock_quantity}`,
      expiry_date: variantStoreProperty?.expiry_date || "",
      discount_rate: `${variantStoreProperty?.discount_rate || ""}`,
    };
    newVariantStoreProperties.push(newStoreProperty);
  });
  return newVariantStoreProperties;
};

const reArrangeVariant = (variants, isEditMode, rootFormDetails = {}) => {
  const newVariants = [];

  variants?.forEach((variant) => {
    const newVariant = {
      ...variant,
      store_properties: reArrangeVariantStoreProperties(
        variant?.store_variant_properties,
        isEditMode,
        rootFormDetails,
      ),
    };
    delete newVariant.store_variant_properties;
    newVariants.push(newVariant);
  });
  return newVariants;
};

const getVariantsProperties = (
  productDetails,
  isEditMode = false,
  rootFormDetails = {},
) => {
  return productDetails?.variants?.length > 0
    ? reArrangeVariant(productDetails?.variants, isEditMode, rootFormDetails)
    : [];
};

const reArrangeStoreProperties = (
  storeProperties,
  isEditMode = false,
  rootFormDetails = {},
) => {
  const newStoreProperties = [];
  storeProperties?.forEach((storeProperty) => {
    const newStoreProperty = {
      ...storeProperty,
      pricings: storeProperty?.pricings?.map((pricingProperty) => {
        return {
          price:
            rootFormDetails?.include_vat_in_price && rootFormDetails?.vat
              ? Number(pricingProperty?.price) +
                (Number(pricingProperty?.price) *
                  Number(rootFormDetails?.vat)) /
                  100
              : Number(pricingProperty?.price),
          // pricingProperty?.operation_unit_price || pricingProperty?.price,
          discount_rate: pricingProperty?.discount_rate,
          pricing_type_id: pricingProperty?.pricing_type?.id,
        };
      }),
      cost_price: `${isEditMode ? storeProperty?.cost_price || 0 : storeProperty?.cost_price || ""}`,
      minimum_stock_quantity:
        storeProperty?.minimum_stock_quantity === null ||
        storeProperty?.minimum_stock_quantity === undefined
          ? undefined
          : `${storeProperty?.minimum_stock_quantity}`,
      rebate: storeProperty?.rebate || "",
      expiry_date: storeProperty?.expiry_date || "",
      discount_rate: `${storeProperty?.discount_rate || ""}`,
      returned_from_backend: true,
    };

    newStoreProperties.push(newStoreProperty);
  });
  return newStoreProperties;
};

const useSingleProductForm = ({
  isEditMode,
  productDetails,
  handleProductUpdate,
  storePropertiesKey = "store_product_properties",
  isServiceType = false,
}) => {
  const { storeList } = useSelector((state) => state.profileDetailsReducer);
  const inputRef = useRef();
  const variantImgRef = useRef([]);
  const [subCategories, setSubCategories] = useState([]);
  const params = useParams();
  const queryClient = useQueryClient();
  const [rootFormDetails, setRootFormDetails] = useState({
    name: productDetails?.name || "",
    description: productDetails?.description || "",
    categories: [
      ...new Set(
        productDetails?.categories?.map(
          (category) => category?.parent_category?.id ?? category?.id,
        ) || [],
      ),
    ],
    sub_categories:
      productDetails?.categories
        ?.filter((category) => category?.parent_category)
        ?.map((category) => category.id) || [],
    sku: productDetails?.sku || "",
    barcode: productDetails?.barcode || "",
    stores: productDetails?.stores
      ? reLabelDefault(productDetails?.stores)
      : [],
    has_variant: productDetails?.variants?.length > 0 ? true : false,
    use_same_pricing: false,
    unit: productDetails?.unit
      ? {
          label: productDetails?.unit?.unit,
          value: productDetails?.unit?.id,
        }
      : "",
    additional_units:
      productDetails?.additional_units?.length > 0
        ? productDetails?.additional_units?.map((additional_unit) => {
            return {
              unit: additional_unit?.unit?.id,
              value: additional_unit?.value,
              uuidv4: uuidv4(),
            };
          })
        : [],
    operation_unit: productDetails?.operation_unit?.id || "",
    show_product_in_sell_mode:
      (productDetails?.show_product_in_sell_mode !== null) &
      (productDetails?.show_product_in_sell_mode !== undefined)
        ? productDetails?.show_product_in_sell_mode
        : true,
    section: productDetails?.section || "",
    image_url: productDetails?.image_url || null,
    media_files: productDetails?.media_files || [],
    vat: productDetails?.vat ? productDetails.vat : "",
    supplier: productDetails?.supplier ? productDetails?.supplier?.id : "",
    supplier_name: productDetails?.supplier
      ? productDetails?.supplier?.name
      : "",
    is_service: productDetails?.is_service || false,
    vat_enabled: productDetails?.vat && productDetails.vat > 0 ? true : false,
    include_vat_in_price: productDetails?.include_vat_in_price || false,
    auto_create_product_on_squareup: false,
  });
  const [variantProperties, setVariantProperties] = useState(
    getVariantsProperties(productDetails, isEditMode, {
      vat: productDetails?.vat,
      include_vat_in_price: productDetails?.include_vat_in_price,
    }),
  );
  const [storeProperties, setStoreProperties] = useState(
    reArrangeStoreProperties(productDetails?.[storePropertiesKey], isEditMode, {
      vat: productDetails?.vat,
      include_vat_in_price: productDetails?.include_vat_in_price,
    }),
  );
  const [isStorePropertiesUpdated, setIsStorePropertiesUpdated] =
    useState(false);
  const [isVariantPropertiesUpdated, setIsVariantPropertiesUpdated] =
    useState(false);
  const [modalViews, setModalViews] = useState({
    isCreatePricing: false,
    isCreateUnit: false,
    isWarning: false,
    isCreateSupplier: false,
  });
  const [productImages, setProductImages] = useState(
    productDetails?.media_files ? productDetails?.media_files : [],
  );

  const { mutate: reorderImages } = useMutation((data) =>
    reorderProductImages(data),
  );
  const triggerToast = useContext(ToastContext);
  const { setRequestLoaderProgress } = useContext(RequestLoader);
  const { data: stockUnits, isLoading: isLoadingStockUnit } =
    useGetStockUnits();
  const { data: pricingTypes, isLoading: isLoadingPricingTypes } =
    useGetPricingTypes();
  const {
    data: categories,
    isLoading: isLoadingCategories,
    refetch: refetchCategories,
  } = useGetProductCategoryList();

  const { loadOptions } = useGetSuppliersForSelectField({});

  const { data: sections, isLoading: isLoadingSections } = useGetSections();
  const { getStoreInfo, getPricingInfo } = useGetInfo();
  const [detectedPricing, setDetectedPricing] = useState([]);

  useEffect(() => {
    if (productDetails) {
      setProductImages(productDetails?.media_files);
    }
  }, [productDetails]);

  // update store properties for product without variants
  const updateStoreProperties = (
    stores = rootFormDetails?.stores,
    useSamePricing = rootFormDetails?.use_same_pricing,
  ) => {
    const newStoreProperties = updateStorePropertiesHelper({
      stores,
      useSamePricing,
      storeProperties: storeProperties,
      storeList,
    });
    setStorePropertiesFunc(newStoreProperties);
  };

  // add a new pricing for a store property
  const addNewPricingForStoreProperty = (storeIndex) => {
    const newStoreProperties = addNewPricingForStorePropertyHelper({
      storeProperties,
      storeIndex,
    });
    setStorePropertiesFunc(newStoreProperties);
  };

  // remove pricing for a store property
  const removePricingForStoreProperty = (storeIndex, pricingIndex) => {
    const newStoreProperties = removePricingForStorePropertyHelper({
      storeProperties,
      storeIndex,
      pricingIndex,
    });
    setStorePropertiesFunc(newStoreProperties);
  };

  // help to set store property when changes are made
  const setStorePropertiesFunc = (newStoreProperties) => {
    setStoreProperties(newStoreProperties);
    setIsStorePropertiesUpdated(false);
    window.setProductFormValue("storeProperties", newStoreProperties);
  };

  const createVariantProperties = (stores = rootFormDetails?.stores) => {
    const newVariant = createVariantPropertiesHelper({ storeList, stores });
    setVariantPropertiesFunc([newVariant]);
  };

  const updateVariantStoreProperties = (stores = rootFormDetails?.stores) => {
    const updatedVariantProperties = updateVariantStorePropertiesHelper({
      stores,
      storeList,
      variantProperties,
    });
    setVariantPropertiesFunc(updatedVariantProperties);
  };

  // toggles "useSameVariantPricing" for a single variant and propagates
  // pricing to other stores immediately, instead of relying on the
  // isVariantPropertiesUpdated flag/effect (which only reacts to a
  // false -> true transition and can silently no-op for later variants).
  const applyVariantPricingAcrossStores = (variantIndex, value) => {
    const newVariantProperties = deepCopyFunction(variantProperties);
    newVariantProperties[variantIndex].useSameVariantPricing = value;

    const updatedVariantProperties = updateVariantStorePropertiesHelper({
      stores: rootFormDetails?.stores,
      storeList,
      variantProperties: newVariantProperties,
    });
    setVariantPropertiesFunc(updatedVariantProperties);
  };

  const addNewVariantOption = (stores = rootFormDetails?.stores) => {
    const newVariant = addNewVariantOptionHelper({ stores, storeList });
    setVariantPropertiesFunc([...variantProperties, newVariant]);
  };

  const deleteVariantProperty = (variantIndex) => {
    const newVariantProperties = removeVariantPropertyHelper({
      variantProperties,
      variantIndex,
    });
    setVariantPropertiesFunc(newVariantProperties);
  };

  const addNewPricingPropertyForVariantStoreProperty = (
    storeIndex,
    variantIndex,
  ) => {
    const newVariantProperties =
      addNewPricingPropertyForVariantStorePropertyHelper({
        storeIndex,
        variantIndex,
        variantProperties,
      });
    setVariantPropertiesFunc(newVariantProperties);
  };

  const removePricingPropertyForVariantStoreProperty = (
    pricingIndex,
    storeIndex,
    variantIndex,
  ) => {
    const newVariantProperties =
      removePricingPropertyForVariantStorePropertyHelper({
        pricingIndex,
        storeIndex,
        variantIndex,
        variantProperties,
      });
    setVariantPropertiesFunc(newVariantProperties);
  };

  // help to set variant store property when changes are made
  const setVariantPropertiesFunc = (variants) => {
    setVariantProperties(variants);
    window.setProductFormValue("variantProperties", variants);
    setIsVariantPropertiesUpdated(false);
  };

  // Image upload function
  const uploadImageMutation = useMutation((data) => uploadProductImage(data));
  const isUploadingImage = uploadImageMutation.isLoading;

  const { mutate: deleteImage } = useMutation({
    mutationFn: (mediaId) => deleteProductImage(mediaId),
    onSuccess: (data) => {
      queryClient.invalidateQueries(["getProductDetails", params?.id]);
      queryClient.invalidateQueries(["getProductList"]);
      triggerToast(data.data.detail, "success");
    },
  });

  const handleVariantImageUpload = (event, variantIndex) => {
    setRequestLoaderProgress(getRandomIntInclusive(10, 40));
    if (event.target.files[0].size > 10000000)
      return new Error("File size should be less than 10mb");
    const file = event.target.files[0];
    const formData = new FormData();
    formData.append("file", file);
    uploadImageMutation(formData, {
      onSuccess: (response) => {
        const url = response?.data?.data?.url;
        const newVariantProperties = [...variantProperties];
        newVariantProperties[variantIndex].image_url = url;
        setVariantProperties(newVariantProperties);
        triggerToast("Image uploaded successfully", "success");
      },
      onSettled: () => {
        setRequestLoaderProgress(100);
      },
    });
  };

  const handleMultipleImageUpload = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const readFile = (file) =>
      new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve({ file, preview: reader.result });
        reader.readAsDataURL(file);
      });

    Promise.all(files.map(readFile)).then((newImages) => {
      setProductImages((prev) => [...prev, ...newImages]);

      newImages.forEach(({ file, preview }) => {
        const formData = new FormData();
        formData.append("file", file);
        if (params?.id) {
          formData.append("item_id", params.id);
          formData.append("item_type", isServiceType ? "service" : "product");
        }

        uploadImageMutation
          .mutateAsync(formData)
          .then((response) => {
            const saved = response?.data?.data;
            setProductImages((prev) =>
              prev.map((img) =>
                img.preview === preview
                  ? {
                      id: saved.id,
                      url: saved.url,
                      file_name: saved.file_name,
                      file: file, // kept so useCreateProduct can link on new product save
                    }
                  : img,
              ),
            );
            triggerToast("Image uploaded successfully", "success");
          })
          .catch(() => {
            setProductImages((prev) =>
              prev.filter((img) => img.preview !== preview),
            );
            triggerToast("Failed to upload image", "error");
          });
      });
    });
  };

  //   Add new category function
  const { mutate: addCategory } = useMutation((data) => createCategory(data));
  const addNewCategory = (value) => {
    setRequestLoaderProgress(getRandomIntInclusive(10, 40));
    const createOption = (label, value) => ({
      label,
      value,
    });
    addCategory(
      { name: value },
      {
        onSuccess: (response) => {
          setRequestLoaderProgress(100);
          const newOption = createOption(
            response?.data?.data?.name,
            response?.data?.data?.id,
          );
          refetchCategories();
          setRootFormDetails((prevState) => ({
            ...prevState,
            category: newOption,
          }));
          triggerToast("Category Added Sucessfully", "success");
        },
        onSettled: () => setRequestLoaderProgress(100),
      },
    );
  };

  // delete variant from backend
  const deleteVariantFromBackend = useDeleteVariantFromBackend();
  const handleDeleteVariantFromBackend = (id) => {
    setRequestLoaderProgress(getRandomIntInclusive(10, 40));
    deleteVariantFromBackend.mutate(
      { id },
      {
        onSuccess: () => {
          triggerToast(
            `Variant with id: ${id} deleted successfully`,
            "success",
          );
        },
        onSettled: () => setRequestLoaderProgress(100),
      },
    );
  };

  const createOrUpdateProduct = () => {
    let newDetectedPricing = [];
    const submissionData = { ...rootFormDetails, media_files: productImages };
    submissionData.barcode = rootFormDetails?.has_variant
      ? undefined
      : submissionData?.barcode;
    submissionData.stores = submissionData?.stores?.map(
      (storeItem) => storeItem?.value,
    );
    submissionData.unit = submissionData?.unit?.value;
    submissionData.additional_units =
      submissionData?.additional_units?.length > 0
        ? submissionData?.additional_units
        : null;
    const cleanSubmissionData = cleanObject(submissionData, [
      "use_same_pricing",
      isServiceType ? "has_variant" : "",
    ]);

    if (!rootFormDetails.has_variant) {
      cleanSubmissionData.store_properties = [...storeProperties]?.map(
        (storeItem) => {
          const pricings = storeItem?.pricings?.map((pricingItem) => {
            if (Number(storeItem?.cost_price) > Number(pricingItem?.price))
              newDetectedPricing.push({
                store_name: storeItem?.store_name,
                store_currency: getStoreInfo(storeItem?.store_id)
                  ?.currency_code,
                cost_price: storeItem?.cost_price,
                selling_price: pricingItem?.price,
                pricing_name: getPricingInfo(pricingItem?.pricing_type_id)
                  ?.label,
              });

            const realSellingPrice =
              rootFormDetails?.include_vat_in_price &&
              rootFormDetails?.vat_enabled
                ? Number(pricingItem?.price) /
                  (1 + Number(rootFormDetails?.vat) / 100)
                : Number(pricingItem?.price);

            return cleanObject({ ...pricingItem, price: realSellingPrice }, [
              "uuid",
            ]);
          });

          return cleanObject(
            {
              ...storeItem,
              rebate: storeItem?.rebate === "" ? "0" : storeItem?.rebate,
              pricings,
            },
            isServiceType ? ["minimum_stock_quantity", "stock_quantity"] : [],
          );
        },
      );
    } else {
      const oldVariants = deepCopyFunction(variantProperties);

      const cleanedOldVariants = oldVariants?.map((variantItem) => {
        const store_properties = variantItem?.store_properties?.map(
          (variantStoreItem) => {
            const pricings = variantStoreItem?.pricings?.map((pricingItem) => {
              if (
                Number(variantStoreItem?.cost_price) >
                Number(pricingItem?.price)
              )
                newDetectedPricing.push({
                  store_name: variantStoreItem?.store_name,
                  store_currency: getStoreInfo(variantStoreItem?.store_id)
                    ?.currency_code,
                  cost_price: variantStoreItem?.cost_price,
                  selling_price: pricingItem?.price,
                  pricing_name: getPricingInfo(pricingItem?.pricing_type_id)
                    ?.label,
                });

              const realSellingPrice =
                rootFormDetails?.include_vat_in_price &&
                rootFormDetails?.vat_enabled
                  ? Number(pricingItem?.price) /
                    (1 + Number(rootFormDetails?.vat) / 100)
                  : Number(pricingItem?.price);

              return cleanObject({ ...pricingItem, price: realSellingPrice }, [
                "uuid",
              ]);
            });

            return cleanObject(
              {
                ...variantStoreItem,
                pricings,
                rebate:
                  variantStoreItem?.rebate === ""
                    ? "0"
                    : variantStoreItem?.rebate,
              },
              ["useSameVariantPricing"],
            );
          },
        );

        return cleanObject({ ...variantItem, store_properties }, [
          "useSameVariantPricing",
          "unit",
          "uuid",
        ]);
      });

      if (isEditMode) {
        submissionData.new_variants = cleanedOldVariants.filter(
          (item) => item?.id === undefined,
        );
        submissionData.variants = cleanedOldVariants.filter((item) => item?.id);
      } else submissionData.variants = cleanedOldVariants;
    }

    // return a warning modal
    if (newDetectedPricing?.length > 0 && !modalViews?.isWarning) {
      setDetectedPricing(newDetectedPricing);
      return resetModalViews("isWarning", true);
    }

    resetModalViews("isWarning", false);
    handleProductUpdate(submissionData);
  };

  const resetModalViews = (key, value = true) => {
    setModalViews({
      isCreatePricing: false,
      isCreateUnit: false,
      isWarning: false,
      [key]: value,
    });
  };

  const addAdditionalUnit = () => {
    setRootFormDetails((prev) => ({
      ...prev,
      additional_units: [
        ...prev?.additional_units,
        { unit: "", value: "", uuidv4: uuidv4() },
      ],
    }));
  };

  const deleteAdditionalUnit = (index) => {
    const newAdditionalUnit = [...rootFormDetails?.additional_units];
    newAdditionalUnit?.splice(index, 1);
    setRootFormDetails((prev) => ({
      ...prev,
      additional_units: newAdditionalUnit,
    }));
  };

  useEffect(() => {
    if (!rootFormDetails.has_variant && isStorePropertiesUpdated)
      updateStoreProperties();
  }, [isStorePropertiesUpdated]);

  useEffect(() => {
    if (rootFormDetails.has_variant && isVariantPropertiesUpdated) {
      if (variantProperties?.length === 0) createVariantProperties();
      else updateVariantStoreProperties();
    }
  }, [isVariantPropertiesUpdated]);

  useEffect(() => {
    if (window.setProductFormValue)
      window.setProductFormValue(
        "additionalUnit",
        rootFormDetails?.additional_units,
      );
  }, [rootFormDetails?.additional_units]);

  return {
    productImages,
    booleanLabel,
    rootFormDetails,
    categories,
    storeList,
    inputRef,
    isUploadingImage,
    variantProperties,
    storeProperties,
    stockUnits,
    isLoadingStockUnit,
    pricingTypes,
    isLoadingPricingTypes,
    isLoadingCategories,
    sections,
    isLoadingSections,
    variantImgRef,
    modalViews,
    detectedPricing,
    subCategoriesList: [
      ...new Map(
        [...(rootFormDetails?.categories || [])]
          .reverse() // most recently selected category first
          .map((categoryId) =>
            categories?.find((c) => `${c.id}` === `${categoryId}`),
          )
          .filter(Boolean)
          .flatMap((category) => category.sub_categories || [])
          .map((sub) => [sub.id, sub]), // Map keeps first occurrence
      ).values(),
    ],
    setProductImages,
    updateStoreProperties,
    createVariantProperties,
    setStoreProperties,
    setVariantProperties,
    setRootFormDetails,
    handleVariantImageUpload,
    createOrUpdateProduct,
    addNewVariantOption,
    updateVariantStoreProperties,
    applyVariantPricingAcrossStores,
    deleteVariantProperty,
    addNewCategory,
    setIsStorePropertiesUpdated,
    setIsVariantPropertiesUpdated,
    addNewPricingForStoreProperty,
    removePricingForStoreProperty,
    addNewPricingPropertyForVariantStoreProperty,
    removePricingPropertyForVariantStoreProperty,
    handleDeleteVariantFromBackend,
    handleMultipleImageUpload,
    deleteImage,
    resetModalViews,
    addAdditionalUnit,
    deleteAdditionalUnit,
    subCategories,
    setSubCategories,
    loadOptions,
    reorderImages,
  };
};

export default useSingleProductForm;
